import { m } from 'framer-motion';
// @mui
import { styled } from '@mui/material/styles';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';
import Grid from '@mui/material/Unstable_Grid2';

// components
import { MotionViewport, varFade } from 'src/components/animate';
import RequestDemo from 'src/components/request-demo';
import Iconify from 'src/components/iconify';

// ----------------------------------------------------------------------

const StyledRoot = styled(Box)(({ theme }) => ({
  position: 'relative',
  overflow: 'hidden',
  borderRadius: theme.spacing(3),
  background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.warning.main} 100%)`,
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'url(/assets/background/overlay_1.svg)',
    backgroundSize: 'cover',
    opacity: 0.1,
  },
}));

const StyledContent = styled(Box)(({ theme }) => ({
  position: 'relative',
  zIndex: 1,
  color: 'white',
}));

// ----------------------------------------------------------------------

export default function HomeCTA() {
  const handleDemoRequest = async (formData) => {
    // Simulate API call
    await new Promise((resolve) => setTimeout(resolve, 2000));
    console.log('Demo request submitted:', formData);

    // Here you would typically send the data to your backend
    // const response = await fetch('/api/demo-requests', {
    //   method: 'POST',
    //   headers: { 'Content-Type': 'application/json' },
    //   body: JSON.stringify(formData),
    // });

    // if (!response.ok) {
    //   throw new Error('Failed to submit demo request');
    // }

    // return response.json();
  };

  return (
    <Box
      sx={{
        py: { xs: 3, md: 6 },
        px: { xs: 0, md: 0 },
        bgcolor: 'background.default',
        width: '100%',
        display: 'flex',
        alignItems: 'center',
        minHeight: { xs: 'auto', md: '100vh' },
      }}
    >
      <Container maxWidth="xl" component={MotionViewport} sx={{ width: '100%' }}>
        <StyledRoot>
          <StyledContent>
            <Grid
              container
              spacing={6}
              alignItems="center"
              sx={{ py: { xs: 3, md: 6 }, width: '100%' }}
            >
              <Grid item xs={12} md={7}>
                <Box sx={{ pl: { xs: 2, md: 4 }, textAlign: { xs: 'center', md: 'left' } }}>
                  <m.div variants={varFade().inLeft}>
                    <Typography
                      variant="h2"
                      sx={{
                        mb: 3,
                        color: 'white',
                        textAlign: { xs: 'center', md: 'left' },
                        fontSize: { xs: '1.75rem', sm: '2.125rem', md: '2.5rem' },
                        px: { xs: 1, sm: 0 },
                      }}
                    >
                      Ready to Transform
                      <br />
                      Your Supply Chain?
                    </Typography>
                  </m.div>

                  <m.div variants={varFade().inLeft}>
                    <Typography
                      variant="h6"
                      sx={{
                        mb: 4,
                        color: 'rgba(255, 255, 255, 0.9)',
                        textAlign: { xs: 'center', md: 'left' },
                        fontSize: { xs: '0.875rem', sm: '1rem', md: '1.25rem' },
                        px: { xs: 2, sm: 0 },
                      }}
                    >
                      Join thousands of restaurants and suppliers who have already streamlined their
                      operations with Cartify. Start your journey today and experience the
                      difference.
                    </Typography>
                  </m.div>

                  <m.div variants={varFade().inLeft}>
                    <Stack
                      direction={{ xs: 'column', sm: 'row' }}
                      spacing={3}
                      sx={{
                        flexWrap: 'wrap',
                        alignItems: { xs: 'center', sm: 'flex-start' },
                        justifyContent: { xs: 'center', sm: 'flex-start' },
                        px: { xs: 2, sm: 0 },
                      }}
                    >
                      <RequestDemo
                        buttonText="Request a Demo"
                        buttonVariant="outlined"
                        buttonColor="inherit"
                        buttonSize="large"
                        startIcon={<Iconify icon="mdi:play-circle" />}
                        onDemoRequest={handleDemoRequest}
                        sx={{
                          py: 1.5,
                          px: 3,
                          fontSize: '1rem',
                          fontWeight: 'bold',
                          borderRadius: 2,
                          minWidth: { xs: '180px', sm: '180px' },
                          maxWidth: { xs: '260px', sm: 'none' },
                          bgcolor: 'transparent',
                          color: 'common.white',
                          border: '2px solid',
                          borderColor: 'common.white',
                          backdropFilter: 'blur(10px)',
                          '&:hover': {
                            bgcolor: 'rgba(255, 255, 255, 0.1)',
                            borderColor: 'primary.light',
                          },
                        }}
                      />
                    </Stack>
                  </m.div>
                </Box>
              </Grid>

              <Grid item xs={12} md={5}>
                <m.div variants={varFade().inRight}>
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      textAlign: 'center',
                      position: 'relative',
                      p: 3,
                      minHeight: { xs: '300px', md: '400px' },
                    }}
                  >
                    <Box
                      component="img"
                      src="/assets/images/home/logo-single2.webp"
                      alt="Cartify Logo"
                      sx={{
                        width: '100%',
                        maxWidth: 300,
                        height: 'auto',
                        filter: 'brightness(0) invert(1)',
                        opacity: 0.9,
                        mb: 3,
                      }}
                    />

                    <Box
                      sx={{
                        position: 'relative',
                        width: '100%',
                        maxWidth: 300,
                        mb: 2,
                      }}
                    >
                      <Typography
                        variant="h6"
                        sx={{
                          color: 'rgba(255, 255, 255, 0.8)',
                          fontSize: { xs: '0.875rem', sm: '1rem' },
                          fontWeight: 500,
                        }}
                      >
                        Trusted by leading restaurants and suppliers worldwide
                      </Typography>
                    </Box>
                  </Box>
                </m.div>
              </Grid>
            </Grid>
          </StyledContent>
        </StyledRoot>
      </Container>
    </Box>
  );
}
